/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  ControlledInput,
  ControlledTextarea,
  DynamicFormInput,
  FormUpload,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useCommunities } from "../../store/useCommunities";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useAlert } from "../../store/useAlert";
import { Switch } from "@material-tailwind/react";
import { useDirectUploadImage } from "../../store/useDirectUploadImage";
import { useDropLocations } from "../../store/useDropLocations";
import { DropLocationInputItem } from "../../components/form/DynamicFormInput";

type Inputs = {
  name: string;
  description: string;
  website: string;
};

function AddCommunityScreen() {
  /** Hooks */
  const { addCommunity, addedCommunity, clearAddedCommunity } =
    useCommunities();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();
  const { uploadImage, clearUploadedImage, uploadedImage, thumbnailImage } =
    useDirectUploadImage();
  const {
    uploadImage: uploadBackgroundImage,
    clearUploadedImage: clearUploadedBackgroundImage,
    uploadedImage: uploadedBackgroundImage,
  } = useDirectUploadImage();
  const { setAlert } = useAlert();
  const { dropLocations, getDropLocations } = useDropLocations();
  /** State */
  const [featuredImage, setFeaturedImage] = useState<File | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [cities, setCities] = useState<DropLocationInputItem[]>([]);

  /** Effects */
  useEffect(() => {
    getDropLocations();
  }, []);

  useEffect(() => {
    if (addedCommunity) {
      clearAddedCommunity();
      clearUploadedImage();
      clearUploadedBackgroundImage();
      navigate(-1);
    }
  }, [addedCommunity]);

  useEffect(() => {
    if (image) {
      onUploadImage();
    }
  }, [image]);

  useEffect(() => {
    if (featuredImage) {
      onUploadBackgroundImage();
    }
  }, [featuredImage]);

  /** Callbacks */
  const goBack = () => navigate(-1);

  const onSubmit = async (data: Inputs) => {
    if (!image) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedImage) {
      setAlert({
        isOpen: true,
        message: "Please upload an image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    if (!uploadedBackgroundImage) {
      setAlert({
        isOpen: true,
        message: "Please upload a featured background image.",
        severity: "error",
      });
      return setSubmitted(true);
    }
    let body: any = {
      ...data,
      image: uploadedImage,
      thumbnail: thumbnailImage ?? uploadedImage,
      isFeatured,
    };
    if (uploadedBackgroundImage) {
      body.featuredImage = uploadedBackgroundImage;
    }
    if (cities?.length) {
      body.cities = cities?.map((input) => input?.id) ?? [];
    }
    addCommunity(body);
  };

  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setImage(e.target.files[0]);
    }
  };

  const onUploadImage = () => {
    if (!image) return;
    uploadImage(image, true);
  };

  const imageError = submitted && !image;

  const onBackgroundImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      setFeaturedImage(e.target.files[0]);
    }
  };

  const onUploadBackgroundImage = () => {
    if (!featuredImage) return;
    uploadBackgroundImage(featuredImage);
  };

  const backgroundImageError = submitted && !featuredImage;

  const citiesToSelect = useMemo(() => {
    if (!dropLocations?.length) return [];
    let locationsToSelect: { value: string; label: string }[] = [];
    for (let location of dropLocations) {
      if (locationsToSelect?.some((loc) => loc?.value === location?.city))
        continue;
      locationsToSelect.push({
        label: location?.city as string,
        value: location?.city as string,
      });
    }
    return locationsToSelect;
  }, [dropLocations]);

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new community
        </h1>
      </div>
      <div className="flex flex-col sm:flex-row">
        <div className="w-full sm:w-1/2 bg-white border border-gray-border drop-shadow-md mb-2 sm:mb-0">
          <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
            <p className="font-satoshi font-medium text-base text-dark">
              New community
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
            <ControlledInput
              control={control}
              error={Boolean(errors?.name)}
              label="Name"
              placeholder="Community name"
              name="name"
              rules={{ required: true }}
            />
            <ControlledInput
              control={control}
              error={Boolean(errors?.website)}
              label="Website"
              placeholder="Community website"
              name="website"
              rules={{
                required: true,
                pattern: /https?:\/\/.+/,
              }}
            />
            <FormUpload
              label="Community logo"
              onChange={onImageChange}
              value={image}
              error={imageError}
            />
            <Switch
              crossOrigin={"anonymous"}
              color="teal"
              ripple
              label="Is featured"
              checked={isFeatured}
              onChange={(e) => setIsFeatured(e?.target.checked)}
            />
            {/* Community featured backround image */}
            <FormUpload
              label="Featured background image"
              onChange={onBackgroundImageChange}
              value={featuredImage}
              error={backgroundImageError}
            />
            <ControlledTextarea
              name="description"
              control={control}
              rules={{ required: true }}
              error={Boolean(errors?.description)}
              label="Description"
              placeholder="Type community description here"
              inputClassName="min-h-[180px]"
              labelClassName="mt-4"
            />
            <DynamicFormInput
              generateIds={false}
              label="Cities"
              inputs={cities}
              setInputs={setCities}
              options={citiesToSelect ?? []}
            />
            <div className="flex flex-row items-center justify-end">
              <SecondaryButton type="button" onClick={goBack} label="Cancel" />
              <PrimaryButton type="submit" className="ml-4" label="Save" />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddCommunityScreen);
